import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSession } from '@/components/auth';
import { invalidateComplaintsAfterTransfer } from '@/hooks/use-complaints';
import { queryKeys } from '@/hooks/use-data';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import {
	accountingService,
	type AccountUpdateBody,
	type CheckIssueResponse,
	type PersonCreateBody,
	type VoidBillResult,
} from '@/services/accounting-service';
import { dataService } from '@/services/data-service';
import { FeatureFlagName, UserRole } from '@/types/models';
import type {
	Account, AccountFlag, AccountPersonRole, BankAccount, BankAccountKind, BankStatementImport, BankStatementImportRow, BankTransfer,
	Bill, BillStatus, BillWithLines,
	Budget, Charge, CollectionsAction, Credit, Deposit, GlCode, GlCodeAccountType, Invoice, InvoiceStatus, InvoiceWithCharges, Item, JournalEntry, MemorizedBill, MemorizedBillWithLines, MemorizedInvoice, MemorizedInvoiceWithLines, MonthClose, Payment, Person, PersonPosition, PersonPositionAssignment, Reconciliation, Refund, ReturnedPayment, Vendor,
	WorkOrderBillingPostResult, WorkOrderBillingPreview, WorkOrderBillingQueueItem, WorkOrderBillingState,
} from '@/types/models';
import type { CheckPrintRequestInput } from '@/types/schemas/check-print';
import { INVOICE_LINE_GL_TYPES } from '@/utils/gl-code-policy';

/**
 * Shared gate: who may modify money-movements, accounts or people. Everyone else
 * with the accounting feature flag can view data and add / edit notes.
 *
 * MUST mirror the server exactly, because this is a WRITE gate: `hasPermission`
 * (api/permissions.ts) answers Admin ROLE, Accounting role, or the
 * `accounting_admin` capability — and nothing else.
 *
 * It used to read `user.isAdmin`, the column #500 RETIRED. The server stopped
 * honouring it in `hasPermission` while this kept trusting it, so the two
 * disagreed: a property manager carrying `is_admin = 1` was shown editable
 * accounting controls and got `403 update_accounts` on save. That is how the
 * association edit page offered Dues and Late-fees to someone who could not
 * persist either. A render guard that outranks its write guard is not a
 * convenience, it is a trap — so the column is not consulted here.
 */
export function useAccountingPermissions() {
	const { user } = useSession();
	const { hasFlag } = useFeatureFlags();
	const role = user?.role;
	// The Admin ROLE alone, matching `api/index.ts`, which gates /admin on
	// `role === UserRole.Admin` and reads the retired column nowhere.
	const isAdmin = role === UserRole.Admin;
	const isSuperAdmin = isAdmin;
	const isAccounting = role === UserRole.Accounting;
	/** The capability that lets a non-Accounting role write accounting data. */
	/**
	 * BOTH flags, mirroring `CAPABILITY_PREREQUISITE_FLAGS` on the server.
	 *
	 * The feature-flag API happily sets `accounting_admin` without `accounting`, and in that
	 * state the server denies every accounting permission. Reading the capability alone here
	 * made `canModify` true against a server that says no — offering writes that always 403,
	 * which is the exact divergence this branch exists to remove.
	 */
	const isAccountingAdmin = hasFlag(FeatureFlagName.AccountingAdmin) && hasFlag(FeatureFlagName.Accounting);
	const canModify = isAdmin || isAccounting || isAccountingAdmin;
	return {
		isAdmin,
		isSuperAdmin,
		isAccounting,
		isAccountingAdmin,
		canModify,
		canDeleteNotes: canModify,
		// Mirrors `POST /accounts/:id/(un)archive`, which gates on UPDATE_ACCOUNTS — a reversible
		// accounting write, not a super-admin power — AFTER the accounting flag: the accounts
		// handler 404s every non-GET without it. `canModify` alone is true for an Accounting-role
		// user whose flag is off, which would offer a button that always 404s.
		canArchiveAccount: canModify && hasFlag(FeatureFlagName.Accounting),
	};
}

const STALE = 30 * 1000;

// Account-tag registry hooks live in their own lightweight module (no auth/
// expo-router chain) so leaf components can use them in router-free unit tests.
// Re-exported here for call sites that import everything from use-accounting.
export { useAccountTags, useCreateAccountTag, useUpdateAccountTag, useDeleteAccountTag } from './use-account-tags';

export function useAccounts(params: Parameters<typeof accountingService.listAccounts>[0] = {}) {
	return useQuery({
		queryKey: ['accounting', 'accounts', params],
		queryFn: () => accountingService.listAccounts(params),
		staleTime: STALE,
	});
}

export function useAccount(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'account', id],
		queryFn: () => accountingService.getAccount(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}

/** Portfolio-wide duplicate-account detection (admin report). */
export function useDuplicateAccounts(options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: ['accounting', 'accounts', 'duplicates'],
		queryFn: () => accountingService.listDuplicateAccounts(),
		enabled: options.enabled ?? true,
		staleTime: STALE,
	});
}

export function useLedger(accountId: string | undefined, asOf?: number) {
	return useQuery({
		queryKey: ['accounting', 'ledger', accountId, asOf ?? null],
		queryFn: () => accountingService.getLedger(accountId!, asOf),
		enabled: !!accountId,
		staleTime: STALE,
	});
}

export function useAccountPeople(accountId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'account-people', accountId],
		queryFn: () => accountingService.listPeople({ accountId }),
		enabled: !!accountId,
		staleTime: STALE,
	});
}

export function useStatements(accountId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'statements', accountId],
		queryFn: () => accountingService.listStatements(accountId!),
		enabled: !!accountId,
		staleTime: STALE,
	});
}

export const useGenerateStatement = () =>
	useAccountingMutation<{ accountId: string; periodStart: number; periodEnd: number }, unknown>(
		(v) => accountingService.generateStatement(v),
	);
export const useDeleteStatement = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteStatement(id) as Promise<void>);

export function useAssociationDues(associationId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'association-dues', associationId],
		queryFn: () => accountingService.getAssociationDues(associationId!),
		enabled: !!associationId,
		staleTime: STALE,
	});
}

/** The portfolio dues schedule (`GET /accounting/post-dues`); fetched only when `enabled`. */
export function useDuesSchedule(enabled: boolean) {
	return useQuery({
		queryKey: ['accounting', 'dues-schedule'],
		queryFn: () => accountingService.getDuesSchedule(),
		enabled,
		staleTime: STALE,
	});
}

export const useUpdateAssociationDues = () =>
	useAccountingMutation<{ associationId: string; body: Parameters<typeof accountingService.updateAssociationDues>[1] }, unknown>(
		({ associationId, body }) => accountingService.updateAssociationDues(associationId, body),
	);
export const useCreateDuesLine = () =>
	useAccountingMutation<{ associationId: string; body: Parameters<typeof accountingService.createDuesLine>[1] }, unknown>(
		({ associationId, body }) => accountingService.createDuesLine(associationId, body),
	);
export const useUpdateDuesLine = () =>
	useAccountingMutation<{ associationId: string; lineId: string; body: Parameters<typeof accountingService.updateDuesLine>[2] }, unknown>(
		({ associationId, lineId, body }) => accountingService.updateDuesLine(associationId, lineId, body),
	);
export const useDeleteDuesLine = () =>
	useAccountingMutation<{ associationId: string; lineId: string }, unknown>(
		({ associationId, lineId }) => accountingService.deleteDuesLine(associationId, lineId) as Promise<unknown>,
	);

export function useLateFeeRules(associationId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'late-fee-rules', associationId],
		queryFn: () => accountingService.listLateFeeRules(associationId!),
		enabled: !!associationId,
		staleTime: STALE,
	});
}
export const useCreateLateFeeRule = () =>
	useAccountingMutation<Parameters<typeof accountingService.createLateFeeRule>[0], Awaited<ReturnType<typeof accountingService.createLateFeeRule>>>(
		(body) => accountingService.createLateFeeRule(body),
	);
export const useUpdateLateFeeRule = () =>
	useAccountingMutation<{ id: string; body: Parameters<typeof accountingService.updateLateFeeRule>[1] }, unknown>(
		({ id, body }) => accountingService.updateLateFeeRule(id, body),
	);
export const useDeleteLateFeeRule = () =>
	useAccountingMutation<string, unknown>(
		(id) => accountingService.deleteLateFeeRule(id) as Promise<unknown>,
	);

export function useFiscalPeriods(associationId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'fiscal-periods', associationId],
		queryFn: () => accountingService.listFiscalPeriods(associationId!),
		enabled: !!associationId,
		staleTime: STALE,
	});
}
export const useCloseFiscalPeriod = () =>
	useAccountingMutation<Parameters<typeof accountingService.closeFiscalPeriod>[0], unknown>(
		(body) => accountingService.closeFiscalPeriod(body),
	);
export const useReopenFiscalPeriod = () =>
	useAccountingMutation<{ id: string; reason: string }, unknown>(
		({ id, reason }) => accountingService.reopenFiscalPeriod(id, { reason }),
	);

// Month-end close (finalize → serialize → lock → archive).
export function useMonthCloses(associationId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'month-closes', associationId],
		queryFn: () => accountingService.listMonthCloses(associationId!),
		enabled: !!associationId,
		staleTime: STALE,
	});
}
export const useFinalizeMonth = () =>
	useAccountingMutation<Parameters<typeof accountingService.finalizeMonth>[0], MonthClose>(
		(body) => accountingService.finalizeMonth(body),
	);
export const useReopenMonthClose = () =>
	useAccountingMutation<{ id: string; reason: string }, unknown>(
		({ id, reason }) => accountingService.reopenMonthClose(id, { reason }),
	);

export function useFunds(associationId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'funds', associationId],
		queryFn: () => accountingService.listFunds(associationId!),
		enabled: !!associationId,
		staleTime: STALE,
	});
}
export const useCreateFund = () =>
	useAccountingMutation<Parameters<typeof accountingService.createFund>[0], unknown>(
		(body) => accountingService.createFund(body),
	);
export const useUpdateFund = () =>
	useAccountingMutation<{ id: string; body: Parameters<typeof accountingService.updateFund>[1] }, unknown>(
		({ id, body }) => accountingService.updateFund(id, body),
	);
export const useDeleteFund = () =>
	useAccountingMutation<string, unknown>((id) => accountingService.deleteFund(id));

export function useJournalEntries(
	associationId: string | undefined,
	params: { page?: number; limit?: number; sourceRefPrefix?: string } = {},
) {
	return useQuery({
		queryKey: ['accounting', 'journal-entries', associationId, params],
		queryFn: () => accountingService.listJournalEntries(associationId!, params),
		enabled: !!associationId,
		staleTime: STALE,
	});
}
export function useJournalEntry(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'journal-entries', id],
		queryFn: () => accountingService.getJournalEntry(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}
export function useBillJournalEntries(billId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'bills', billId, 'journal-entries'],
		queryFn: () => accountingService.listBillJournalEntries(billId!),
		enabled: !!billId,
		staleTime: STALE,
	});
}
export const useCreateJournalEntry = () =>
	useAccountingMutation<Parameters<typeof accountingService.createJournalEntry>[0], JournalEntry>(
		(body) => accountingService.createJournalEntry(body),
	);
export const useUpdateJournalEntry = () =>
	useAccountingMutation<{ id: string; body: Parameters<typeof accountingService.updateJournalEntry>[1] }, JournalEntry>(
		({ id, body }) => accountingService.updateJournalEntry(id, body),
	);
export const useDeleteJournalEntry = () =>
	useAccountingMutation<string, unknown>((id) => accountingService.deleteJournalEntry(id));

export function useBudgets(associationId: string | undefined, params: { fiscalYear?: number; page?: number; limit?: number } = {}) {
	return useQuery({
		queryKey: ['accounting', 'budgets', associationId, params],
		queryFn: () => accountingService.listBudgets(associationId!, params),
		enabled: !!associationId,
		staleTime: STALE,
	});
}
export function useBudget(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'budget', id],
		queryFn: () => accountingService.getBudget(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}
export const useCreateBudget = () =>
	useAccountingMutation<Parameters<typeof accountingService.createBudget>[0], Budget>(
		(body) => accountingService.createBudget(body),
	);
export const useUpdateBudget = () =>
	useAccountingMutation<{ id: string; body: Parameters<typeof accountingService.updateBudget>[1] }, Budget>(
		({ id, body }) => accountingService.updateBudget(id, body),
	);
export const useDeleteBudget = () =>
	useAccountingMutation<string, unknown>((id) => accountingService.deleteBudget(id));

export function useBankImports(bankAccountId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'bank-imports', bankAccountId],
		queryFn: () => accountingService.listBankImports(bankAccountId!),
		enabled: !!bankAccountId,
		staleTime: STALE,
	});
}
export function useBankImport(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'bank-import', id],
		queryFn: () => accountingService.getBankImport(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}
export const useCreateBankImport = () =>
	useAccountingMutation<Parameters<typeof accountingService.createBankImport>[0], BankStatementImport>(
		(body) => accountingService.createBankImport(body),
	);
export const useUpdateBankImportRow = () =>
	useAccountingMutation<
		{ importId: string; rowId: string; body: Parameters<typeof accountingService.updateBankImportRow>[2] },
		BankStatementImportRow
	>(({ importId, rowId, body }) => accountingService.updateBankImportRow(importId, rowId, body));
export const useDeleteBankImport = () =>
	useAccountingMutation<string, unknown>((id) => accountingService.deleteBankImport(id));

export function useCollectionsActions(params: { accountId?: string; associationId?: string; actionType?: string } = {}) {
	return useQuery({
		queryKey: ['accounting', 'collections-actions', params],
		queryFn: () => accountingService.listCollectionsActions(params),
		staleTime: STALE,
	});
}
export const useCreateCollectionsAction = () =>
	useAccountingMutation<Parameters<typeof accountingService.createCollectionsAction>[0], CollectionsAction>(
		(body) => accountingService.createCollectionsAction(body),
	);
export const useUpdateCollectionsAction = () =>
	useAccountingMutation<{ id: string; body: Parameters<typeof accountingService.updateCollectionsAction>[1] }, CollectionsAction>(
		({ id, body }) => accountingService.updateCollectionsAction(id, body),
	);
export const useDeleteCollectionsAction = () =>
	useAccountingMutation<string, unknown>((id) => accountingService.deleteCollectionsAction(id));

export function useCheckQueue(associationId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'check-queue', associationId],
		queryFn: () => accountingService.listCheckQueue(associationId!),
		enabled: !!associationId,
		staleTime: STALE,
	});
}
export const useIssueChecks = () =>
	useAccountingMutation<CheckPrintRequestInput, CheckIssueResponse>(
		(body) => accountingService.issueChecks(body),
	);

export function useAccountNotes(accountId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'account-notes', accountId],
		queryFn: () => accountingService.listAccountNotes(accountId!),
		enabled: !!accountId,
		staleTime: STALE,
	});
}

export function useAssociationDashboard(associationId: string | undefined, asOf?: number) {
	return useQuery({
		queryKey: ['accounting', 'association-dashboard', associationId, asOf ?? null],
		queryFn: () => accountingService.reportAssociationDashboard(associationId!, asOf),
		enabled: !!associationId,
		staleTime: STALE,
	});
}

export function useOverviewReport() {
	return useQuery({
		queryKey: ['accounting', 'report', 'overview'],
		queryFn: () => accountingService.reportOverview(),
		staleTime: STALE,
	});
}

export function usePortfolioReport() {
	return useQuery({
		queryKey: ['accounting', 'report', 'portfolio'],
		queryFn: () => accountingService.reportPortfolio(),
		staleTime: STALE,
	});
}

export function useDelinquencyReport(params: Parameters<typeof accountingService.reportDelinquency>[0] = {}) {
	return useQuery({
		queryKey: ['accounting', 'report', 'delinquency', params],
		queryFn: () => accountingService.reportDelinquency(params),
		staleTime: STALE,
	});
}

export function useBudgetVsActualReport(
	params: Parameters<typeof accountingService.reportBudgetVsActual>[0] | undefined,
) {
	return useQuery({
		queryKey: ['accounting', 'report', 'budget-vs-actual', params],
		queryFn: () => accountingService.reportBudgetVsActual(params!),
		enabled: !!params?.associationId && !!params?.fiscalYear,
		staleTime: STALE,
	});
}

export function useAgingReport(params: Parameters<typeof accountingService.reportAging>[0] = {}) {
	return useQuery({
		queryKey: ['accounting', 'report', 'aging', params],
		queryFn: () => accountingService.reportAging(params),
		staleTime: STALE,
		// The detail variant can take a moment on large associations; don't
		// retry-storm the worker if the SQL chokes — surface the error instead.
		retry: 1,
	});
}

export function useApAgingReport(params: Parameters<typeof accountingService.reportApAging>[0] = {}) {
	return useQuery({
		queryKey: ['accounting', 'report', 'ap-aging', params],
		queryFn: () => accountingService.reportApAging(params),
		staleTime: STALE,
		retry: 1,
	});
}

export function usePaymentHistoryReport(params: Parameters<typeof accountingService.reportPaymentHistory>[0] = {}) {
	return useQuery({
		queryKey: ['accounting', 'report', 'payment-history', params],
		queryFn: () => accountingService.reportPaymentHistory(params),
		staleTime: STALE,
	});
}

export function useProfitLossReport(
	params: Parameters<typeof accountingService.reportProfitLoss>[0] | undefined,
) {
	return useQuery({
		queryKey: ['accounting', 'report', 'profit-loss', params],
		queryFn: () => accountingService.reportProfitLoss(params!),
		enabled: !!params && isFinite(params.startDate) && isFinite(params.endDate),
		staleTime: STALE,
	});
}

export function useBalanceSheetReport(
	params: Parameters<typeof accountingService.reportBalanceSheet>[0] | undefined = {},
) {
	return useQuery({
		queryKey: ['accounting', 'report', 'balance-sheet', params],
		queryFn: () => accountingService.reportBalanceSheet(params ?? {}),
		enabled: !!params,
		staleTime: STALE,
	});
}

export function use1099Report(
	params: Parameters<typeof accountingService.report1099>[0] = {},
) {
	return useQuery({
		queryKey: ['accounting', 'report', '1099', params],
		queryFn: () => accountingService.report1099(params),
		staleTime: STALE,
	});
}

export function useForm1099Summary(
	params: Parameters<typeof accountingService.report1099Summary>[0] | undefined,
) {
	return useQuery({
		queryKey: ['accounting', 'report', '1099-summary', params],
		queryFn: () => accountingService.report1099Summary(params!),
		enabled: !!params?.associationId,
		staleTime: STALE,
	});
}

export function useJournalReport(
	params: Parameters<typeof accountingService.reportJournal>[0] | undefined,
) {
	return useQuery({
		queryKey: ['accounting', 'report', 'journal', params],
		queryFn: () => accountingService.reportJournal(params!),
		enabled: !!params?.associationId,
		staleTime: STALE,
		retry: 1,
	});
}

/**
 * Invalidate every accounting query. Call after any write so lists, ledgers,
 * and reports refetch together — it's cheap enough at POC scale not to bother
 * with per-resource targeting.
 */
export function useInvalidateAccounting() {
	const qc = useQueryClient();
	return () => qc.invalidateQueries({ queryKey: ['accounting'] });
}

/**
 * Typed mutation helpers. Each wraps the corresponding service call and
 * invalidates the `accounting` query tree on success so consumers don't
 * have to remember to do it themselves.
 */
/** `alsoInvalidate`: query keys outside `['accounting']` whose server answer the write changes. */
function useAccountingMutation<V, R>(fn: (v: V) => Promise<R>, alsoInvalidate: string[][] = []) {
	const invalidate = useInvalidateAccounting();
	const qc = useQueryClient();
	return useMutation<R, Error, V>({
		mutationFn: fn,
		onSuccess: () => {
			for (const queryKey of alsoInvalidate) qc.invalidateQueries({ queryKey });
			return invalidate();
		},
	});
}

// Accounts
type AccountCreateBody = Omit<Partial<Account>, 'archivedAt'> & { archivedAt?: number | null };
export const useCreateAccount = () =>
	useAccountingMutation<AccountCreateBody, Account>((body) => accountingService.createAccount(body));
export const useUpdateAccount = () =>
	useAccountingMutation<{ id: string; body: AccountUpdateBody }, Account>(
		({ id, body }) => accountingService.updateAccount(id, body),
	);
export const useDeleteAccount = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteAccount(id) as Promise<void>);
export const useSetAccountFlags = () =>
	useAccountingMutation<{ id: string; flags: AccountFlag[] }, { ok: true; flags: AccountFlag[] }>(
		({ id, flags }) => accountingService.setAccountFlags(id, flags),
	);
export const useArchiveAccount = () =>
	useAccountingMutation<string, Account>((id) => accountingService.archiveAccount(id));
export const useUnarchiveAccount = () =>
	useAccountingMutation<string, Account>((id) => accountingService.unarchiveAccount(id));
export const useTransferAccount = () => {
	const invalidate = useInvalidateAccounting();
	const queryClient = useQueryClient();
	return useMutation<
		Awaited<ReturnType<typeof accountingService.transferAccount>>,
		Error,
		{ id: string; body: Parameters<typeof accountingService.transferAccount>[1] }
	>({
		mutationFn: ({ id, body }) => accountingService.transferAccount(id, body),
		onSuccess: () => {
			invalidate();
			// The transfer also moves open complaints to the buyer (#566) — outside `accounting`.
			invalidateComplaintsAfterTransfer(queryClient);
		},
	});
};
export const useReapplyAccountPayments = () =>
	useAccountingMutation<string, { accountId: string; paymentsTouched: number; chargesTouched: number }>(
		(id) => accountingService.reapplyAccountPayments(id),
	);

// Charges / Payments / Credits / Refunds
export function useCharges(
	params: Parameters<typeof accountingService.listCharges>[0] = {},
) {
	const enabled = !!(params.associationId || params.accountId);
	return useQuery({
		queryKey: ['accounting', 'charges', 'list', params],
		queryFn: () => accountingService.listCharges(params),
		enabled,
		staleTime: STALE,
	});
}

export const useCreateCharge = () =>
	useAccountingMutation<Partial<Charge>, Charge>((body) => accountingService.createCharge(body));
export const useUpdateCharge = () =>
	useAccountingMutation<{ id: string; body: Partial<Charge> }, Charge>(
		({ id, body }) => accountingService.updateCharge(id, body),
	);
export const useDeleteCharge = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteCharge(id) as Promise<void>);
export const useWaiveCharge = () =>
	useAccountingMutation<{ id: string; reason?: string }, Charge>(
		({ id, reason }) => accountingService.waiveCharge(id, reason),
	);
export const useUnwaiveCharge = () =>
	useAccountingMutation<string, Charge>((id) => accountingService.unwaiveCharge(id));
export const useSetChargeWrittenOff = () =>
	useAccountingMutation<{ id: string; writtenOff: boolean }, Charge>(
		({ id, writtenOff }) => accountingService.setChargeWrittenOff(id, writtenOff),
	);

export const useCreatePayment = () =>
	useAccountingMutation<Partial<Payment>, Payment>((body) => accountingService.createPayment(body));
export const useUpdatePayment = () =>
	useAccountingMutation<{ id: string; body: Partial<Payment> }, Payment>(
		({ id, body }) => accountingService.updatePayment(id, body),
	);
export const useDeletePayment = () =>
	useAccountingMutation<string, void>((id) => accountingService.deletePayment(id) as Promise<void>);
export const useVoidPayment = () =>
	useAccountingMutation<{ id: string; reason?: string }, Payment>(
		({ id, reason }) => accountingService.voidPayment(id, reason),
	);
export const useReturnPayment = () =>
	useAccountingMutation<Parameters<typeof accountingService.returnPayment>[0], ReturnedPayment>(
		(body) => accountingService.returnPayment(body),
	);
export const useUndoReturnedPayment = () =>
	useAccountingMutation<string, void>((id) => accountingService.undoReturnedPayment(id) as Promise<void>);
export function useCredit(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'credit', id],
		queryFn: () => accountingService.getCredit(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}

export const useCreateCredit = () =>
	useAccountingMutation<Partial<Credit>, Credit>((body) => accountingService.createCredit(body));
export const useUpdateCredit = () =>
	useAccountingMutation<{ id: string; body: Partial<Credit> }, Credit>(
		({ id, body }) => accountingService.updateCredit(id, body),
	);
export const useDeleteCredit = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteCredit(id) as Promise<void>);

export const useCreateRefund = () =>
	useAccountingMutation<Partial<Refund>, Refund>((body) => accountingService.createRefund(body));
export const useUpdateRefund = () =>
	useAccountingMutation<{ id: string; body: Partial<Refund> }, Refund>(
		({ id, body }) => accountingService.updateRefund(id, body),
	);
export const useDeleteRefund = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteRefund(id) as Promise<void>);

// Deposits
export function useUndepositedPayments(associationId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'deposits', 'undeposited', associationId],
		queryFn: () => accountingService.listUndepositedPayments(associationId!),
		enabled: !!associationId,
		staleTime: 10 * 1000,
	});
}
export function useDeposits(params: { associationId?: string; limit?: number } = {}) {
	return useQuery({
		queryKey: ['accounting', 'deposits', params],
		queryFn: () => accountingService.listDeposits(params),
		staleTime: 30 * 1000,
	});
}
export function useDeposit(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'deposit', id],
		queryFn: () => accountingService.getDeposit(id!),
		enabled: !!id,
	});
}
export function useDepositPayments(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'deposit-payments', id],
		queryFn: () => accountingService.getDepositPayments(id!),
		enabled: !!id,
	});
}
export const useCreateDeposit = () =>
	useAccountingMutation<
		{ associationId: string; bankAccountId: string; depositedOn: number; memo?: string; paymentIds: string[] },
		Deposit & { paymentIds: string[] }
	>((body) => accountingService.createDeposit(body));
export const useUpdateDeposit = () =>
	useAccountingMutation<{ id: string; body: { bankAccountId?: string; depositedOn?: number; memo?: string | null } }, Deposit>(
		({ id, body }) => accountingService.updateDeposit(id, body),
	);
export const useMarkDepositPrinted = () =>
	useAccountingMutation<string, Deposit>((id) => accountingService.markDepositPrinted(id));
export const useDeleteDeposit = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteDeposit(id) as Promise<void>);

// People
export const useCreatePerson = () =>
	useAccountingMutation<PersonCreateBody, Person>((body) => accountingService.createPerson(body));
export const useUpdatePerson = () =>
	useAccountingMutation<{ id: string; body: Partial<Person> }, Person>(
		({ id, body }) => accountingService.updatePerson(id, body),
	);
export const useAttachPerson = () =>
	useAccountingMutation<{ personId: string; accountId: string; role: AccountPersonRole; importBatchId?: string; relationship?: string }, unknown>(
		({ personId, accountId, role, importBatchId, relationship }) =>
			accountingService.attachPerson(personId, accountId, role, importBatchId, relationship),
	);
export const useDetachPerson = () =>
	useAccountingMutation<{ personId: string; accountId: string; role?: AccountPersonRole }, unknown>(
		({ personId, accountId, role }) => accountingService.detachPerson(personId, accountId, role),
	);

// Person-positions lookup vocabulary.
export function usePersonPositions(search?: string) {
	return useQuery({
		queryKey: ['accounting', 'person-positions', search ?? ''],
		queryFn: () => accountingService.listPersonPositions({ q: search }),
		staleTime: STALE,
	});
}
export const useCreatePersonPosition = () =>
	useAccountingMutation<{ name: string; sortOrder?: number }, PersonPosition>(
		(body) => accountingService.createPersonPosition(body),
	);

// Person → Position assignments (junction with start/end dates).
export function usePersonPositionAssignments(personId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'person-position-assignments', personId],
		queryFn: () => accountingService.listPersonPositionAssignments(personId!),
		enabled: !!personId,
		staleTime: STALE,
	});
}
export const useCreatePersonPositionAssignment = () =>
	useAccountingMutation<
		{ personId: string; body: { positionId: string; startDate?: number; endDate?: number; notes?: string } },
		PersonPositionAssignment
	>(({ personId, body }) => accountingService.createPersonPositionAssignment(personId, body));
export const useUpdatePersonPositionAssignment = () =>
	useAccountingMutation<
		{ personId: string; assignmentId: string; body: { positionId?: string; startDate?: number; endDate?: number; notes?: string } },
		PersonPositionAssignment
	>(({ personId, assignmentId, body }) => accountingService.updatePersonPositionAssignment(personId, assignmentId, body));
export const useDeletePersonPositionAssignment = () =>
	useAccountingMutation<{ personId: string; assignmentId: string }, unknown>(
		({ personId, assignmentId }) => accountingService.deletePersonPositionAssignment(personId, assignmentId),
	);

// Officer roll-up for the association dashboard.
export function useAssociationOfficers(associationId: string | undefined, activeOnly = true) {
	return useQuery({
		queryKey: ['accounting', 'association-officers', associationId, activeOnly],
		queryFn: () => accountingService.listAssociationOfficers(associationId!, activeOnly),
		enabled: !!associationId,
		staleTime: STALE,
	});
}

// Notes
export const useCreateAccountNote = () =>
	useAccountingMutation<{ accountId: string; body: string }, unknown>(
		(v) => accountingService.createAccountNote(v),
	);
export const useUpdateAccountNote = () =>
	useAccountingMutation<{ id: string; body: string }, unknown>(
		({ id, body }) => accountingService.updateAccountNote(id, { body }),
	);
export const useDeleteAccountNote = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteAccountNote(id) as Promise<void>);

// GL codes (Chart of Accounts)
export function useGlCodes(params: Parameters<typeof accountingService.listGlCodes>[0] = {}) {
	return useQuery({
		queryKey: ['accounting', 'gl-codes', params],
		queryFn: () => accountingService.listGlCodes(params),
		staleTime: STALE,
	});
}
/**
 * Active GL codes a customer invoice line may post to (income + expense-like),
 * ordered by `INVOICE_LINE_GL_TYPES`. One query per type so each keeps its own
 * 500-row page and shares cache with plain `useGlCodes({ accountType })` calls.
 */
export function useInvoiceLineGlCodes(): GlCode[] {
	return useQueries({ queries: INVOICE_LINE_GL_QUERIES, combine: flattenGlCodePages });
}
// Module-level: the query set is static, and a stable `combine` lets TanStack memoize the result.
const INVOICE_LINE_GL_QUERIES = INVOICE_LINE_GL_TYPES.map((accountType) => {
	const params = { accountType, activeOnly: true, limit: 500 };
	return {
		queryKey: ['accounting', 'gl-codes', params],
		queryFn: () => accountingService.listGlCodes(params),
		staleTime: STALE,
	};
});
const flattenGlCodePages = (results: { data?: { data?: GlCode[] } }[]): GlCode[] =>
	results.flatMap((r) => r.data?.data ?? []);
export const useCreateGlCode = () =>
	useAccountingMutation<Partial<GlCode>, GlCode>((body) => accountingService.createGlCode(body));
export const useUpdateGlCode = () =>
	useAccountingMutation<{ id: string; body: Partial<GlCode> }, GlCode>(
		({ id, body }) => accountingService.updateGlCode(id, body),
	);
export const useDeleteGlCode = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteGlCode(id) as Promise<void>);
export const useBulkUpsertGlCodes = () =>
	useAccountingMutation<
		{ rows: Array<Partial<GlCode> & { code: string; name: string; accountType: GlCodeAccountType }>; importBatchId?: string },
		{ inserted: number; updated: number; skipped?: number; total: number }
	>(({ rows, importBatchId }) => accountingService.bulkUpsertGlCodes(rows, { importBatchId }));

// Items — QB-style line-item presets (code → description + GL + default price)
export function useItems(params: Parameters<typeof accountingService.listItems>[0] = {}) {
	return useQuery({
		queryKey: ['accounting', 'items', params],
		queryFn: () => accountingService.listItems(params),
		staleTime: STALE,
	});
}
export function useItem(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'item', id],
		queryFn: () => accountingService.getItem(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}
export const useCreateItem = () =>
	useAccountingMutation<Partial<Item>, Item>((body) => accountingService.createItem(body));
export const useUpdateItem = () =>
	useAccountingMutation<{ id: string; body: Partial<Item> }, Item>(
		({ id, body }) => accountingService.updateItem(id, body),
	);
export const useDeleteItem = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteItem(id) as Promise<void>);

// Vendors
export function useVendors(params: Parameters<typeof accountingService.listVendors>[0] = {}) {
	return useQuery({
		queryKey: ['accounting', 'vendors', params],
		queryFn: () => accountingService.listVendors(params),
		staleTime: STALE,
	});
}
export function useVendor(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'vendor', id],
		queryFn: () => accountingService.getVendor(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}
export const useCreateVendor = () =>
	useAccountingMutation<Partial<Vendor>, Vendor>((body) => accountingService.createVendor(body));
export const useUpdateVendor = () =>
	useAccountingMutation<{ id: string; body: Partial<Vendor> }, Vendor>(
		({ id, body }) => accountingService.updateVendor(id, body),
	);
/**
 * Deleting the org's self vendor flips `organization.selfVendorActive`, which lives under
 * `['organization']`, not `['accounting']` — without this the side nav keeps a link to it.
 *
 * Every write that can soft-delete a vendor passes these — including an import-batch revert
 * (`vendors` is in `TAGGED_TABLES`, api/import-batches.ts); see `useRevertImportBatch`.
 */
const SELF_VENDOR_KEYS = [['organization']];
export const useDeleteVendor = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteVendor(id) as Promise<void>, SELF_VENDOR_KEYS);
export const usePurgeVendor = () =>
	useAccountingMutation<string, Awaited<ReturnType<typeof accountingService.purgeVendor>>>(
		(id) => accountingService.purgeVendor(id),
		SELF_VENDOR_KEYS,
	);
/**
 * Revert an import batch: soft-deletes every row tagged with it, vendors included — so it can
 * remove the org's self vendor, and refreshes `['organization']` like a vendor delete does.
 */
export const useRevertImportBatch = () =>
	useAccountingMutation<string, Awaited<ReturnType<typeof accountingService.revertImportBatch>>>(
		(id) => accountingService.revertImportBatch(id),
		SELF_VENDOR_KEYS,
	);
export const useBulkUpsertVendors = () =>
	useAccountingMutation<
		{ rows: Array<Partial<Vendor> & { name: string }>; importBatchId?: string },
		{ inserted: number; updated: number; total: number; errors: Array<{ name: string; externalId?: string; error: string }> }
	>(({ rows, importBatchId }) => accountingService.bulkUpsertVendors(rows, importBatchId));

export const useBulkImportCustomers = () =>
	useAccountingMutation<
		Parameters<typeof accountingService.bulkImportCustomers>[0],
		Awaited<ReturnType<typeof accountingService.bulkImportCustomers>>
	>((params) => accountingService.bulkImportCustomers(params));

// Bank accounts
export function useBankAccounts(
	params: Parameters<typeof accountingService.listBankAccounts>[0] = {},
	options?: { enabled?: boolean },
) {
	return useQuery({
		queryKey: ['accounting', 'bank-accounts', params],
		queryFn: () => accountingService.listBankAccounts(params),
		staleTime: STALE,
		enabled: options?.enabled ?? true,
	});
}
export function useBankAccount(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'bank-account', id],
		queryFn: () => accountingService.getBankAccount(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}
export function useBankSubGlBalances(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'bank-sub-gl', id],
		queryFn: () => accountingService.getBankSubGlBalances(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}
type BankAccountCreateBody = Parameters<typeof accountingService.createBankAccount>[0];
type BankAccountUpdateBody = Parameters<typeof accountingService.updateBankAccount>[1];
export const useCreateBankAccount = () =>
	useAccountingMutation<BankAccountCreateBody, BankAccount>((body) => accountingService.createBankAccount(body));
export const useUpdateBankAccount = () =>
	useAccountingMutation<{ id: string; body: BankAccountUpdateBody }, BankAccount>(
		({ id, body }) => accountingService.updateBankAccount(id, body),
	);
export const useDeleteBankAccount = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteBankAccount(id) as Promise<void>);
export const useSetDefaultBankAccount = () =>
	useAccountingMutation<string, BankAccount>((id) => accountingService.setDefaultBankAccount(id));
/**
 * `placeholderData` keeps the previous page mounted while a page, rows-per-page or TYPE change
 * loads (#562, #490). Without it the new key's `data` is undefined for the whole fetch, and
 * the register's `?? 0` fallbacks render a PLAUSIBLE WRONG NUMBER — a $0.00 posted balance, a
 * 0-transaction count, a pager fed 0 total rows that resets its own page — while `isLoading`
 * swaps the table for a spinner. Only the SAME bank's data may stand in: carrying one bank's
 * balance onto another bank's page would be a wrong number of its own.
 */
export function useBankAccountRegister(
	id: string | undefined,
	params: Parameters<typeof accountingService.getBankAccountRegister>[1] = {},
) {
	return useQuery({
		queryKey: ['accounting', 'bank-account-register', id, params],
		queryFn: () => accountingService.getBankAccountRegister(id!, params),
		enabled: !!id,
		staleTime: STALE,
		placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[2] === id ? previous : undefined),
	});
}
export const useRecomputeBankAccountBalance = () =>
	useAccountingMutation<string, { bankAccountId: string; currentBalanceCents: number }>(
		(id) => accountingService.recomputeBankAccountBalance(id),
	);

export const useSweepUndepositedPayments = () =>
	useAccountingMutation<string, { bankAccountId: string; depositsCreated: number; paymentsLinked: number; totalCents: number }>(
		(id) => accountingService.sweepUndepositedPayments(id),
	);

// Bank transfers
export function useBankTransfers(params: { associationId?: string; bankAccountId?: string } = {}) {
	return useQuery({
		queryKey: ['accounting', 'bank-transfers', params],
		queryFn: () => accountingService.listBankTransfers(params),
		staleTime: STALE,
	});
}
type BankTransferCreateBody = Parameters<typeof accountingService.createBankTransfer>[0];
type BankTransferUpdateBody = Parameters<typeof accountingService.updateBankTransfer>[1];
export const useCreateBankTransfer = () =>
	useAccountingMutation<BankTransferCreateBody, BankTransfer>((body) => accountingService.createBankTransfer(body));
export const useUpdateBankTransfer = () =>
	useAccountingMutation<{ id: string; body: BankTransferUpdateBody }, BankTransfer>(
		({ id, body }) => accountingService.updateBankTransfer(id, body),
	);
export const useDeleteBankTransfer = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteBankTransfer(id) as Promise<void>);

// Reconciliations
export function useReconciliations(bankAccountId: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'reconciliations', bankAccountId],
		queryFn: () => accountingService.listReconciliations(bankAccountId!),
		enabled: !!bankAccountId,
		staleTime: STALE,
	});
}
export function useReconciliation(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'reconciliation', id],
		queryFn: () => accountingService.getReconciliation(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}
export const useCreateReconciliation = () =>
	useAccountingMutation<Parameters<typeof accountingService.createReconciliation>[0], Reconciliation>(
		(body) => accountingService.createReconciliation(body),
	);
export const useUpdateReconciliation = () =>
	useAccountingMutation<{ id: string; body: Parameters<typeof accountingService.updateReconciliation>[1] }, Reconciliation>(
		({ id, body }) => accountingService.updateReconciliation(id, body),
	);
export const useFinishReconciliation = () =>
	useAccountingMutation<
		{ id: string; cleared: Array<{ kind: 'payment' | 'bill_payment' | 'transfer_out' | 'transfer_in' | 'journal' | 'returned_payment' | 'deposit'; transactionId: string }> },
		Reconciliation
	>(({ id, cleared }) => accountingService.finishReconciliation(id, { cleared }));
export const useUndoReconciliation = () =>
	useAccountingMutation<string, Reconciliation>((id) => accountingService.undoReconciliation(id));
export const useDeleteReconciliation = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteReconciliation(id) as Promise<void>);
export type { Reconciliation };
// Surface BankAccountKind from types as a re-export so consumers don't have
// to also import it from @/types/models (matches the AccountStatus pattern).
export type { BankAccount, BankAccountKind };

// Bills (AP)
export function useBills(params: Parameters<typeof accountingService.listBills>[0] = {}) {
	return useQuery({
		queryKey: ['accounting', 'bills', params],
		queryFn: () => accountingService.listBills(params),
		staleTime: STALE,
	});
}
export function useBill(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'bill', id],
		queryFn: () => accountingService.getBill(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}
export const useCreateBill = () =>
	useAccountingMutation<Parameters<typeof accountingService.createBill>[0], BillWithLines>(
		(body) => accountingService.createBill(body),
	);
export const useUpdateBill = () =>
	useAccountingMutation<{ id: string; body: Parameters<typeof accountingService.updateBill>[1] }, BillWithLines>(
		({ id, body }) => accountingService.updateBill(id, body),
	);
export const useDeleteBill = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteBill(id) as Promise<void>);
export const usePurgeBill = () =>
	useAccountingMutation<string, Awaited<ReturnType<typeof accountingService.purgeBill>>>(
		(id) => accountingService.purgeBill(id),
	);
export const useVoidBill = () =>
	useAccountingMutation<string, VoidBillResult>((id) => accountingService.voidBill(id));
export const useVoidCheck = () =>
	useAccountingMutation<string, { voided: true; id: string; paymentsReversed: number }>(
		(id) => accountingService.voidCheck(id),
	);
export const usePayBill = () =>
	useAccountingMutation<
		{ id: string; body: Parameters<typeof accountingService.payBill>[1] },
		Awaited<ReturnType<typeof accountingService.payBill>>
	>(({ id, body }) => accountingService.payBill(id, body));
export type { Bill, BillStatus, BillWithLines };

// Work-order billing — approved work orders becoming bills.
//
// Query keys spread `params` into a stable object rather than embedding an
// inline literal, so a re-render with the same filters reuses the cache instead
// of refetching forever.
//
// `enabled` is caller-controlled for the sidebar badge: the endpoint 404s for anyone
// without the accounting flag, and a failed query would render as a spurious error.
export function useWorkOrderBillingQueue(
	params: Parameters<typeof accountingService.workOrderBillingQueue>[0] = {},
	enabled = true,
) {
	return useQuery({
		queryKey: queryKeys.workOrderBilling.queue(params as Record<string, unknown>),
		queryFn: () => accountingService.workOrderBillingQueue(params),
		enabled,
		staleTime: STALE,
	});
}
export function useWorkOrderBillingPreview(workOrderId: string | undefined) {
	return useQuery({
		queryKey: queryKeys.workOrderBilling.preview(workOrderId),
		queryFn: () => accountingService.workOrderBillingPreview(workOrderId!),
		enabled: !!workOrderId,
		staleTime: STALE,
	});
}
/**
 * A work order's billing state, for the work-order detail card.
 *
 * `enabled` is caller-controlled so the work-order page can skip the request
 * entirely for users without the accounting flag — those endpoints answer 404
 * by design, and a failed query would render as a spurious error.
 */
export function useWorkOrderBillingState(workOrderId: string | undefined, enabled = true) {
	return useQuery({
		queryKey: ['accounting', 'work-order-billing', 'state', workOrderId],
		queryFn: () => accountingService.workOrderBillingState(workOrderId!),
		enabled: !!workOrderId && enabled,
		staleTime: STALE,
	});
}
export function useNextInvoiceNumber(enabled = true) {
	return useQuery({
		queryKey: ['accounting', 'work-order-billing', 'next-number'],
		queryFn: () => accountingService.workOrderBillingNextNumber(),
		enabled,
		staleTime: 0,
	});
}
export const usePostWorkOrderBill = () =>
	useAccountingMutation<
		{ workOrderId: string; body?: Parameters<typeof accountingService.postWorkOrderBill>[1] },
		WorkOrderBillingPostResult
	>(({ workOrderId, body }) => accountingService.postWorkOrderBill(workOrderId, body ?? {}));
export const useCommitWorkOrderBills = () =>
	useAccountingMutation<
		Parameters<typeof accountingService.commitWorkOrderBills>[0],
		Awaited<ReturnType<typeof accountingService.commitWorkOrderBills>>
	>((body) => accountingService.commitWorkOrderBills(body));
export const useSkipWorkOrderBilling = () =>
	useAccountingMutation<
		{ workOrderId: string; reason?: string },
		Awaited<ReturnType<typeof accountingService.skipWorkOrderBilling>>
	>(({ workOrderId, reason }) => accountingService.skipWorkOrderBilling(workOrderId, reason));
export const useMarkWorkOrderBilled = () =>
	useAccountingMutation<
		{ workOrderId: string; reference?: string },
		Awaited<ReturnType<typeof accountingService.markWorkOrderBilled>>
	>(({ workOrderId, reference }) => accountingService.markWorkOrderBilled(workOrderId, reference));
export const useUnskipWorkOrderBilling = () =>
	useAccountingMutation<string, Awaited<ReturnType<typeof accountingService.unskipWorkOrderBilling>>>(
		(workOrderId) => accountingService.unskipWorkOrderBilling(workOrderId),
	);
/**
 * Save a customer-facing description WITHOUT declaring the work order ready (#496).
 *
 * A reviewer often wants to park a half-written rewrite and come back to it. Routed
 * through the ordinary work-order PUT, where the field is gated on REVIEW_WORK_ORDERS —
 * the transition endpoints are for transitions, and using one here would send the property
 * manager a push about a draft.
 */
export const useUpdateCustomerDescription = () =>
	useAccountingMutation<{ workOrderId: string; customerDescription: string }, unknown>(
		({ workOrderId, customerDescription }) =>
			dataService.updateWorkOrder(workOrderId, { customerDescription }),
	);

export type { WorkOrderBillingPostResult, WorkOrderBillingPreview, WorkOrderBillingQueueItem, WorkOrderBillingState };

// Invoices (AR — wraps existing charges as line items)
export function useInvoices(params: Parameters<typeof accountingService.listInvoices>[0] = {}) {
	return useQuery({
		queryKey: ['accounting', 'invoices', params],
		queryFn: () => accountingService.listInvoices(params),
		staleTime: STALE,
	});
}
export function useInvoice(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'invoice', id],
		queryFn: () => accountingService.getInvoice(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}
export const useCreateInvoice = () =>
	useAccountingMutation<Parameters<typeof accountingService.createInvoice>[0], InvoiceWithCharges>(
		(body) => accountingService.createInvoice(body),
	);
export const useUpdateInvoice = () =>
	useAccountingMutation<{ id: string; body: Parameters<typeof accountingService.updateInvoice>[1] }, InvoiceWithCharges>(
		({ id, body }) => accountingService.updateInvoice(id, body),
	);
export const useDeleteInvoice = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteInvoice(id) as Promise<void>);
export const useVoidInvoice = () =>
	useAccountingMutation<string, Invoice>((id) => accountingService.voidInvoice(id));
export const usePurgeInvoice = () =>
	useAccountingMutation<string, { purged: boolean; id: string; accountsRebuilt: string[] }>(
		(id) => accountingService.purgeInvoice(id),
	);
export const useSetInvoicePrintQueue = () =>
	useAccountingMutation<{ id: string; queued: boolean }, Invoice>(
		({ id, queued }) => accountingService.setInvoicePrintQueue(id, queued),
	);
export const useBulkMarkInvoicesPrinted = () =>
	useAccountingMutation<string[], { updated: number }>(
		(ids) => accountingService.bulkMarkInvoicesPrinted(ids),
	);
export type { Invoice, InvoiceStatus, InvoiceWithCharges };

// Memorized (recurring) invoices
export function useMemorizedInvoices(params: Parameters<typeof accountingService.listMemorizedInvoices>[0] = {}) {
	return useQuery({
		queryKey: ['accounting', 'memorized-invoices', params],
		queryFn: () => accountingService.listMemorizedInvoices(params),
		staleTime: STALE,
	});
}
export function useMemorizedInvoice(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'memorized-invoice', id],
		queryFn: () => accountingService.getMemorizedInvoice(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}
export const useCreateMemorizedInvoice = () =>
	useAccountingMutation<Parameters<typeof accountingService.createMemorizedInvoice>[0], MemorizedInvoiceWithLines>(
		(body) => accountingService.createMemorizedInvoice(body),
	);
export const useUpdateMemorizedInvoice = () =>
	useAccountingMutation<{ id: string; body: Parameters<typeof accountingService.updateMemorizedInvoice>[1] }, MemorizedInvoiceWithLines>(
		({ id, body }) => accountingService.updateMemorizedInvoice(id, body),
	);
export const useDeleteMemorizedInvoice = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteMemorizedInvoice(id) as Promise<void>);
export type { MemorizedInvoice, MemorizedInvoiceWithLines };

// Memorized (recurring) bills
export function useMemorizedBills(params: Parameters<typeof accountingService.listMemorizedBills>[0] = {}) {
	return useQuery({
		queryKey: ['accounting', 'memorized-bills', params],
		queryFn: () => accountingService.listMemorizedBills(params),
		staleTime: STALE,
	});
}
export function useMemorizedBill(id: string | undefined) {
	return useQuery({
		queryKey: ['accounting', 'memorized-bill', id],
		queryFn: () => accountingService.getMemorizedBill(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}
export const useCreateMemorizedBill = () =>
	useAccountingMutation<Parameters<typeof accountingService.createMemorizedBill>[0], MemorizedBillWithLines>(
		(body) => accountingService.createMemorizedBill(body),
	);
export const useUpdateMemorizedBill = () =>
	useAccountingMutation<{ id: string; body: Parameters<typeof accountingService.updateMemorizedBill>[1] }, MemorizedBillWithLines>(
		({ id, body }) => accountingService.updateMemorizedBill(id, body),
	);
export const useDeleteMemorizedBill = () =>
	useAccountingMutation<string, void>((id) => accountingService.deleteMemorizedBill(id) as Promise<void>);
export type { MemorizedBill, MemorizedBillWithLines };
